import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { 
  LayoutDashboard, 
  Building2, 
  TrendingUp, 
  Target, 
  DollarSign,
  Menu,
  X,
  Activity,
  Droplets,
  BarChart3,
  Map,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const navigation = [
  { name: 'Overview', href: '/', icon: LayoutDashboard },
  { name: 'Operators', href: '/operators', icon: Building2 },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Map', href: '/map', icon: Map },
  { name: 'Forecast', href: '/forecasting', icon: TrendingUp },
  { name: 'Benchmark', href: '/benchmarking', icon: Target },
  { name: 'Economics', href: '/economics', icon: DollarSign },
  { name: 'Export', href: '/export', icon: Download },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const { 
    sidebarOpen, 
    toggleSidebar, 
    priceDeck, 
    setPriceDeck,
    lastUpdate,
    isLoading 
  } = useStore();

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar - Collapsed by default, icon-only on desktop */}
      <aside 
        className={cn(
          "fixed lg:relative inset-y-0 left-0 z-50 flex flex-col bg-surface border-r border-white/10 transition-all duration-300",
          sidebarOpen ? "w-48" : "w-0 lg:w-14"
        )}
      >
        {/* Logo */}
        <div className={cn(
          "flex items-center h-12 px-3 border-b border-white/10",
          !sidebarOpen && "lg:justify-center"
        )}>
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Forecast Wells" className="w-6 h-6 flex-shrink-0" />
              <span className="font-heading font-bold text-xs tracking-widest uppercase text-foreground truncate">
                Forecast Wells
              </span>
            </div>
          ) : (
            <div className="hidden lg:flex items-center justify-center">
              <img src="/logo.png" alt="Forecast Wells" className="w-6 h-6" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-1.5 py-2 space-y-0.5 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/' && location.pathname.startsWith(item.href));
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className={cn(
                  "flex items-center gap-2 px-2.5 py-2 rounded-sm text-xs font-medium transition-all duration-200",
                  isActive 
                    ? "bg-primary/10 text-primary" 
                    : "text-muted-foreground hover:bg-white/5 hover:text-foreground",
                  !sidebarOpen && "lg:justify-center lg:px-0"
                )}
                title={item.name}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {sidebarOpen && (
                  <span className="tracking-wide truncate">{item.name}</span>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header - Compact for mobile */}
        <header className="flex items-center justify-between h-12 px-3 lg:px-4 bg-surface border-b border-white/10">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              data-testid="toggle-sidebar"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
            
            {/* Real-time indicator - Compact */}
            <div className="flex items-center gap-1.5">
              <div className={cn(
                "w-1.5 h-1.5 rounded-full",
                isLoading ? "bg-primary animate-pulse" : "bg-success pulse-indicator"
              )} />
              <span className="text-[10px] text-muted-foreground font-mono uppercase">
                {isLoading ? 'Sync' : 'Live'}
              </span>
              <span className="text-[10px] text-muted-foreground font-mono hidden sm:inline">
                {formatTime(lastUpdate)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Price Deck Toggle - Compact */}
            <Select 
              value={priceDeck.toString()} 
              onValueChange={(val) => setPriceDeck(Number(val))}
            >
              <SelectTrigger 
                data-testid="price-deck-select"
                className="w-16 sm:w-20 h-7 bg-surface-highlight border-white/10 text-xs font-mono px-2"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-white/10">
                <SelectItem value="60" className="font-mono text-xs">$60</SelectItem>
                <SelectItem value="70" className="font-mono text-xs">$70</SelectItem>
                <SelectItem value="75" className="font-mono text-xs">$75</SelectItem>
                <SelectItem value="80" className="font-mono text-xs">$80</SelectItem>
                <SelectItem value="90" className="font-mono text-xs">$90</SelectItem>
                <SelectItem value="100" className="font-mono text-xs">$100</SelectItem>
              </SelectContent>
            </Select>

            {/* Refresh indicator - Hidden on mobile */}
            <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-sm bg-surface-highlight border border-white/10">
              <Activity className="w-3 h-3 text-success" />
              <span className="text-[10px] font-mono text-muted-foreground">60s</span>
            </div>
          </div>
        </header>

        {/* Page content - Optimized padding for mobile */}
        <main className="flex-1 overflow-y-auto p-3 lg:p-5 bg-background">
          <div className="max-w-[1920px] mx-auto animate-fade-in">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
    </div>
  );
};

export default Layout;
